import {
  Component,
  inject,
  input,
  output,
  signal,
  viewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { IonButton, IonIcon, IonModal } from '@ionic/angular/standalone';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { UserGender } from '@/interfaces/schema';
import { HealthScoreDisclaimerComponent } from '@components/health-score/disclaimer/disclaimer.component';
import { Session } from '@providers/session/session.service';
import { AvatarNotificationModalActions } from './notification-modal.actions';

/**
 * Avatar notification modal component.
 * Displays a modal prompting users to create their avatar.
 * Based on User Story 5 requirements and Sequence Diagram #1.
 *
 * @exports
 * @class AvatarNotificationModalComponent
 */
@Component({
  selector: 'app-avatar-notification-modal',
  templateUrl: './notification-modal.component.html',
  styleUrls: ['./notification-modal.component.scss'],
  standalone: true,
  imports: [IonModal, IonButton, IonIcon, HealthScoreDisclaimerComponent]
})
export class AvatarNotificationModalComponent {
  private readonly router = inject(Router);
  private readonly session = inject(Session);
  private readonly store = inject(Store);

  private readonly confirmModal = viewChild.required<IonModal>('confirmModal');

  readonly isOpen = input.required<boolean>();

  readonly isOpenChange = output<boolean>();

  protected readonly disclaimer = signal<boolean>(false);
  protected readonly showConfirmation = signal<boolean>(false);

  destroy$ = new Subject<boolean>();

  /**
   * Gets the avatar image path based on gender from profile in local storage.
   *
   * @returns {string} The path to the avatar image.
   * @memberof AvatarNotificationModalComponent
   */
  getAvatarImage(): string {
    const profile = this.session.getProfile();
    const gender = profile?.gender;

    return gender === UserGender.Male
      ? 'assets/images/avatar/avatar-scan-male.png'
      : 'assets/images/avatar/avatar-scan-female.png';
  }

  /**
   * Cancel confirmation dialog.
   * Returns to main modal.
   *
   * @memberof AvatarNotificationModalComponent
   */
  handleCancelConfirmation(): void {
    this.showConfirmation.set(false);
    // Show the main modal again
    this.isOpenChange.emit(true);
    // Dismiss the confirmation modal
    this.confirmModal().dismiss();
  }

  /**
   * Handle X button click.
   * Hides main modal and shows confirmation dialog.
   *
   * @memberof AvatarNotificationModalComponent
   */
  handleCloseButton(): void {
    // Hide the main modal first
    this.isOpenChange.emit(false);
    // Then show the confirmation modal
    this.showConfirmation.set(true);
  }

  /**
   * Confirmation: "I will do it later".
   * Records CLOSE_LATER interaction and closes modal.
   *
   * @memberof AvatarNotificationModalComponent
   */
  handleCloseLater(): void {
    this.store.dispatch(
      AvatarNotificationModalActions.recordInteraction({
        action: 'CLOSE_LATER'
      })
    );
    this.showConfirmation.set(false);
    // Main modal is already closed, just ensure state is updated
    this.store.dispatch(
      AvatarNotificationModalActions.setModalOpen({ isOpen: false })
    );
  }

  /**
   * Confirmation: "Skip" (permanent).
   * Records CLOSE_SKIP interaction and closes modal.
   *
   * @memberof AvatarNotificationModalComponent
   */
  handleCloseSkip(): void {
    this.store.dispatch(
      AvatarNotificationModalActions.recordInteraction({ action: 'CLOSE_SKIP' })
    );
    this.showConfirmation.set(false);
    // Main modal is already closed, just ensure state is updated
    this.store.dispatch(
      AvatarNotificationModalActions.setModalOpen({ isOpen: false })
    );
  }

  /**
   * Handle "Let's get started" button click.
   * Records STARTED interaction and navigates to avatar creation.
   *
   * @memberof AvatarNotificationModalComponent
   */
  handleLetGetStarted(): void {
    this.store.dispatch(
      AvatarNotificationModalActions.recordInteraction({ action: 'STARTED' })
    );
    // Close modal
    this.isOpenChange.emit(false);
    // Navigate to avatar creation page
    this.router.navigate(['/avatar/create']);
  }

  /**
   * Handle "I will do it later" button click.
   * Records REMIND_LATER interaction and closes modal.
   *
   * @memberof AvatarNotificationModalComponent
   */
  handleRemindLater(): void {
    this.store.dispatch(
      AvatarNotificationModalActions.recordInteraction({
        action: 'REMIND_LATER'
      })
    );
    this.isOpenChange.emit(false);
  }

  /**
   * Called when modal is dismissed.
   * Clean up subscriptions.
   *
   * @memberof AvatarNotificationModalComponent
   */
  onDidDismissModal(): void {
    this.isOpenChange.emit(false);
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  /**
   * Called when modal is presented.
   * Records SHOWN interaction for analytics.
   *
   * @memberof AvatarNotificationModalComponent
   */
  onDidPresentModal(): void {
    this.store.dispatch(
      AvatarNotificationModalActions.recordInteraction({ action: 'SHOWN' })
    );
  }
}
